/* eslint react-hooks/exhaustive-deps: "warn" */
import { useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './TransferPricingRegis.module.scss';

import { type QueryRow, type RegisStatus } from './TransferPricingRegis.types';

const ASK =
  'Find German and Polish wholesale distributors of building materials, turnover between 8 and 120 million euro, three years of full accounts, nothing owned more than 25 percent by a single shareholder, and drop anything that manufactures.';

const ROWS: QueryRow[] = [
  { key: 'country', value: 'DE, PL' },
  { key: 'activity_include', value: 'wholesale, distribution, building materials, stockist' },
  { key: 'activity_exclude', value: 'manufacturing', emphasis: true },
  { key: 'turnover', value: '8,000,000 – 120,000,000 EUR' },
  { key: 'years_available', value: '>= 3, full accounts only' },
  { key: 'max_holder_pct', value: '< 25' },
  { key: 'group_member', value: 'exclude', emphasis: true },
  { key: 'financial_years', value: 'FY2023 – FY2025' },
];

const TYPE_DURATION = 3200;
const ROW_STEP = 140;
const HOLD = 4200;

export const TransferPricingRegis = () => {
  const { t } = useInt();
  const rootRef = useRef<HTMLDivElement>(null);
  const askRef = useRef<HTMLSpanElement>(null);
  const cursorRef = useRef<HTMLSpanElement>(null);

  const [status, setStatus] = useState<RegisStatus>('listening');
  const [replyOn, setReplyOn] = useState(false);
  const [paramsOn, setParamsOn] = useState(false);
  const [visibleRows, setVisibleRows] = useState(0);
  const [resultOn, setResultOn] = useState(false);

  useEffect(() => {
    const root = rootRef.current;

    if (!root) {
      return undefined;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let timers: ReturnType<typeof setTimeout>[] = [];
    let raf: number | null = null;
    let active = false;

    const clearAll = () => {
      timers.forEach(clearTimeout);
      timers = [];

      if (raf) {
        cancelAnimationFrame(raf);
        raf = null;
      }
    };

    const at = (ms: number, fn: () => void) => {
      timers.push(setTimeout(fn, ms));
    };

    const settle = () => {
      if (askRef.current) {
        askRef.current.textContent = ASK;
      }

      cursorRef.current?.classList.add(styles.cursorOff);
      setStatus('compiled');
      setReplyOn(true);
      setParamsOn(true);
      setVisibleRows(ROWS.length);
      setResultOn(true);
    };

    const play = () => {
      clearAll();

      if (askRef.current) {
        askRef.current.textContent = '';
      }

      cursorRef.current?.classList.remove(styles.cursorOff);
      setStatus('listening');
      setReplyOn(false);
      setParamsOn(false);
      setVisibleRows(0);
      setResultOn(false);

      let t0: number | null = null;

      const tick = (ts: number) => {
        if (t0 === null) {
          t0 = ts;
        }

        const p = Math.min((ts - t0) / TYPE_DURATION, 1);
        const i = Math.round(ASK.length * p);

        if (askRef.current) {
          askRef.current.textContent = ASK.slice(0, i);
        }

        if (p < 1) {
          raf = requestAnimationFrame(tick);
        } else {
          cursorRef.current?.classList.add(styles.cursorOff);
          setStatus('compiling');

          at(300, () => setReplyOn(true));

          at(420, () => {
            setParamsOn(true);
            ROWS.forEach((_, index) => {
              at(index * ROW_STEP, () => setVisibleRows((prev) => Math.max(prev, index + 1)));
            });
          });

          at(420 + ROWS.length * ROW_STEP + 220, () => {
            setResultOn(true);
            setStatus('compiled');
          });

          at(420 + ROWS.length * ROW_STEP + HOLD, play);
        }
      };

      raf = requestAnimationFrame(tick);
    };

    if (reduceMotion) {
      settle();

      return undefined;
    }

    if (!('IntersectionObserver' in window)) {
      play();

      return () => clearAll();
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!active) {
              active = true;
              play();
            }
          } else {
            active = false;
            clearAll();
          }
        });
      },
      { threshold: 0.25 },
    );

    observer.observe(root);

    return () => {
      clearAll();
      observer.disconnect();
    };
  }, []);

  return (
    <Section className={styles.section}>
      <div className={styles.head}>
        <TitleBlock
          align="left"
          className={styles.titleBlock}
          title={t('Describe the search. Keep the query.')}
          description={
            <>
              <b>{t('Regis is our own AI model, trained on the primary registry data behind the platform')}</b>{' '}
              {t(
                'rather than on the open web. Ask for a search strategy in plain language and it compiles into the same structured query the filter rail produces.',
              )}
              <br />
              <br />
              {t(
                'That distinction matters more here than almost anywhere else. A general assistant can produce a plausible comparable that does not exist. Regis resolves to records that do, each with the filing behind it.',
              )}
            </>
          }
        />
      </div>

      <div className={styles.card} ref={rootRef}>
        <div className={styles.cardHead}>
          <span className={styles.dot} />
          <span className={styles.name}>Regis</span>
          <span className={styles.subtitle}>Trained on government registry data</span>
          <span className={cn(styles.status, styles[`status_${status}`])}>{status}</span>
        </div>

        <div className={styles.body}>
          <div className={styles.left}>
            <div className={styles.label}>Asked</div>

            <div className={styles.ask}>
              <span ref={askRef} />
              <span ref={cursorRef} className={styles.cursor} />
            </div>

            <div className={cn(styles.reply, replyOn && styles.replyOn)}>
              <div className={styles.replyHead}>
                <span className={styles.replyIcon} />
                Regis
              </div>

              <p>
                Population built from <b>612,400</b> active companies across DE and PL. Activity matched on described
                business with manufacturing excluded, then screened on turnover band, filing completeness and
                shareholding.
              </p>

              <p>Every financial field in the result carries the filing it was digitised from.</p>
            </div>
          </div>

          <div className={styles.right}>
            <div className={styles.queryHead}>
              <span className={styles.label}>Compiled query</span>
              <span className={cn(styles.paramCount, paramsOn && styles.paramCountOn)}>{ROWS.length} parameters</span>
            </div>

            <div className={styles.query}>
              {ROWS.map((row, index) => (
                <div key={row.key} className={cn(styles.row, index < visibleRows && styles.rowOn)}>
                  <span className={styles.rowKey}>{row.key}</span>
                  <span className={cn(styles.rowValue, row.emphasis && styles.rowValueEmphasis)}>{row.value}</span>
                </div>
              ))}
            </div>

            <div className={cn(styles.result, resultOn && styles.resultOn)}>
              <span className={styles.resultBig}>86</span>
              <span className={styles.resultLabel}>candidates before functional review</span>
              <span className={styles.resultRef}>stored as TP-2026-0418 · re-runnable against this vintage</span>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
};
